import * as React from 'react';
import { action, observable, makeObservable } from 'mobx';
import { animated, useSpring } from 'react-spring';
import Slide1 from './images/onbording_01.jpg';
import Slide2 from './images/onbording_02.jpg';
import Slide3 from './images/onbording_03.jpg';
import Slide4 from './images/onbording_04.jpg';
import { observer } from 'mobx-react';
import { Button } from '../../../../commonv3/components/atoms/buttons/Button';
import classNames from 'classnames';
import './welcomePane.module.scss';

type StepSpec = {
  index: number;
  header: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  text: any;
  imageSrc: string;
};

type StepProps = StepSpec & { active: boolean };

const Step = ({ header, text, imageSrc, active }: StepProps) => {
  const props = useSpring({ left: active ? '0' : '100%' });
  return (
    <animated.div style={props} styleName="step">
      <div styleName="image-wrapper">
        <img src={imageSrc} styleName="image" />
      </div>
      <div styleName="step-body">
        <div styleName="header">{header}</div>
        <div styleName="text">{text}</div>
      </div>
    </animated.div>
  );
};

type StepListProps = {
  steps: StepSpec[];
  currentIndex: number;
};

const Steps = ({ steps, currentIndex }: StepListProps) => {
  return (
    <div styleName="steps">
      {steps.map((step) => (
        <Step
          key={step.index}
          index={step.index}
          header={step.header}
          text={step.text}
          imageSrc={step.imageSrc}
          active={step.index <= currentIndex}
        />
      ))}
    </div>
  );
};

const StepIndicator = ({ stepNum, currentIndex, onStepClick }) => (
  <div styleName="step-indicator">
    {[...new Array(stepNum).keys()].map((_, index) => (
      <div
        key={index}
        styleName={classNames('item', { active: index + 1 === currentIndex })}
        onClick={() => onStepClick(index + 1)}
      />
    ))}
  </div>
);

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onComplete: () => any;
};

export const WelcomePane = observer(
  class WelcomePane extends React.Component<Props> {
    stepIndex = 1;
    steps: StepSpec[] = [
      {
        index: 1,
        header: '気になるコースで学ぼう',
        text: (
          <span>
            デザインやマーケティングなど様々な分野から
            <br />
            好きなコースを選んで学び始めよう
          </span>
        ),
        imageSrc: Slide1,
      },
      {
        index: 2,
        header: 'レッスンの内容を実際にやってみよう',
        text: (
          <span>
            レッスンごとに学んだことを
            <br />
            実際に手を動かしてやってみましょう。
          </span>
        ),
        imageSrc: Slide2,
      },
      {
        index: 3,
        header: '目標を決めて1ヶ月ごとに振り返ろう',
        text: (
          <span>
            &quot;グループコーチング”を通して学習計画を立てよう
            <br />
            まずは「Welcomeワークショップ」の予約からスタート
          </span>
        ),
        imageSrc: Slide3,
      },
      {
        index: 4,
        header: '試験に挑戦しよう',
        text: (
          <span>
            コースの最後にある試験に取り組んで
            <br />
            身につけたことを実践してみましょう
          </span>
        ),
        imageSrc: Slide4,
      },
    ];

    constructor(props: Props) {
      super(props);

      makeObservable(this, {
        stepIndex: observable,
        steps: observable,
        goBack: action.bound,
        next: action.bound,
      });
    }

    goBack() {
      this.stepIndex = Math.max(this.stepIndex - 1, 1);
    }
    next() {
      this.stepIndex = Math.min(this.stepIndex + 1, this.steps.length);
    }
    render() {
      const { onComplete } = this.props;
      return (
        <div styleName="pane">
          <Steps steps={this.steps} currentIndex={this.stepIndex} />
          <div styleName="footer">
            {this.stepIndex > 1 ? (
              <Button styleName="footer-button" type="link" size="s" onClick={this.goBack}>
                戻る
              </Button>
            ) : (
              <span styleName="footer-button">&nbsp;</span>
            )}
            <StepIndicator
              stepNum={this.steps.length}
              currentIndex={this.stepIndex}
              onStepClick={(s) => (this.stepIndex = s)}
            />
            {this.stepIndex < this.steps.length && (
              <Button styleName="footer-button" type="normal" size="s" onClick={this.next}>
                次へ
              </Button>
            )}
            {this.stepIndex === this.steps.length && (
              <Button styleName="footer-button" type="primary" size="s" onClick={onComplete}>
                はじめる
              </Button>
            )}
          </div>
        </div>
      );
    }
  }
);
